import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Play } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageTitle, StatusBadge } from "@/components/StatusBadge";
import { AddressSearch } from "@/components/AddressSearch";
import { ImportAreaDialog } from "@/components/ImportAreaDialog";
import { Input } from "@/components/ui/input";

export default function Adressen() {
  const [q, setQ] = useState("");
  const [wp, setWp] = useState("");
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["addresses", q, wp], queryFn: () => api.get("/addresses", { params: { q, woonplaats: wp, limit: 200 } }).then((r) => r.data) });
  const { data: wps = [] } = useQuery({ queryKey: ["woonplaatsen"], queryFn: () => api.get("/addresses/woonplaatsen").then((r) => r.data) });

  const analyze = async () => {
    try {
      const { data: j } = await api.post("/analyze/batch", { woonplaats: wp || null });
      toast.success(`Analyse gestart voor ${j.total} adressen`);
      qc.invalidateQueries({ queryKey: ["jobs"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <PageTitle eyebrow="BAG-register · Kadastrale percelen" title="Adressen & percelen">
        <ImportAreaDialog />
        <button data-testid="analyze-filtered-btn" onClick={analyze} className="flex items-center gap-2 h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-500 text-sm font-medium">
          <Play className="h-4 w-4" /> Analyseer {wp || "alle"}
        </button>
      </PageTitle>
      <div className="grid md:grid-cols-[2fr_1fr_1fr] gap-3 mb-6">
        <AddressSearch onImported={() => qc.invalidateQueries({ queryKey: ["addresses"] })} />
        <Input data-testid="filter-addresses-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter op adres of perceel…" className="h-10 bg-slate-950 border-slate-800" />
        <select data-testid="filter-woonplaats-select" value={wp} onChange={(e) => setWp(e.target.value)} className="h-10 rounded-md bg-slate-950 border border-slate-800 px-3 text-sm">
          <option value="">Alle woonplaatsen</option>
          {wps.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
      </div>
      <div className="border border-slate-800 rounded-lg overflow-x-auto bg-[#111827]/80">
        <table className="w-full text-sm" data-testid="addresses-table">
          <thead className="text-left font-mono text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
            <tr><th className="px-4 py-3">Adres</th><th className="px-4 py-3">Perceel</th><th className="px-4 py-3">Opp.</th><th className="px-4 py-3">Woonplaats</th><th className="px-4 py-3">Status</th></tr>
          </thead>
          <tbody>
            {(data?.items || []).map((a) => (
              <tr key={a.id} onClick={() => nav(`/adressen/${a.id}`)} data-testid={`address-row-${a.id}`} className="border-b border-slate-800/60 hover:bg-blue-600/5 cursor-pointer">
                <td className="px-4 py-3 font-medium">{a.weergavenaam}</td>
                <td className="px-4 py-3 font-mono text-xs text-sky-300">{a.perceel?.aanduiding || <span className="text-rose-400">niet gekoppeld</span>}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-400">{a.perceel?.grootte_m2 ? `${a.perceel.grootte_m2} m²` : "–"}</td>
                <td className="px-4 py-3 text-slate-400">{a.woonplaats}</td>
                <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && data.items.length === 0 && <div className="p-10 text-center text-sm text-slate-500" data-testid="addresses-empty">Nog geen adressen. Zoek een adres of importeer een woonplaats.</div>}
        {data && <div className="px-4 py-2 font-mono text-[11px] text-slate-500 border-t border-slate-800" data-testid="addresses-total">{data.total} adressen</div>}
      </div>
    </div>
  );
}
